import { Card } from '@mui/material';
import { QFA_FORM_FIELD_CLASS } from '@qfa/forms';
import clsx from 'clsx';
import { ContentHeader } from 'components/ContentHeader';
import { getScrollParent, hasParent } from 'kineticUtils/elements';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import useFormSectionCardStyles from './styles';
import { FormSectionCardProps } from './types';

const FormSectionCard: React.FunctionComponent<FormSectionCardProps> = (props) => {
  const {
    title,
    children,
    hashRoute,
    addons,
    ignoreHash = false,
    additionalCardClasses,
    additionalContentClasses,
  } = props;

  const classes = useFormSectionCardStyles(props);
  const [isSelected, setSelected] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const skipFocusRef = useRef(false);

  const { hash } = useLocation();

  useEffect(() => {
    if (ignoreHash) {
      return;
    }
    setSelected(hashRoute === hash.substr(1));
  }, [hash, ignoreHash, hashRoute]);

  useEffect(() => {
    if (ignoreHash) {
      return;
    }
    if (isSelected) {
      const card = cardRef.current;
      const skipFocus = skipFocusRef.current;
      if (card && !skipFocus) {
        const inputs: HTMLInputElement[] = Array.prototype.slice.call(
          card.getElementsByTagName('input'),
          0
        );
        // make sure we're focusing the first enabled input
        const input = inputs.find((el) => !el.disabled);

        // MAGIC_NUMBER - height of the embeddedView title
        const yOffset = 36;
        const scrollParent = getScrollParent(card);
        scrollParent.scrollTo({ top: card.offsetTop - yOffset, behavior: 'smooth' });

        input?.focus({ preventScroll: true });
      }
    }
    skipFocusRef.current = false;
  }, [hashRoute, ignoreHash, isSelected]);

  const { push } = useHistory();
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // if the element clicked is within a Form field, assume the user is clicking on the field, and don't trigger the focus

      if (ignoreHash) {
        return;
      }

      const isFormField = (el: { classList: DOMTokenList }) =>
        el.classList.contains(QFA_FORM_FIELD_CLASS);

      if (hasParent(e.target as Element, isFormField, 10)) {
        skipFocusRef.current = true;
      }

      if (hashRoute !== hash.substr(1)) {
        push(`#${hashRoute}`);
      }
    },
    [hashRoute, ignoreHash, hash, push]
  );

  return (
    <Card
      className={clsx(additionalCardClasses, classes.card, {
        [classes.cardSelected]: isSelected,
      })}
      onClick={onClick}
      ref={cardRef}
      component='section'
    >
      <ContentHeader addons={addons} variant='body1' id={hashRoute} className={classes.title}>
        {title}
      </ContentHeader>

      <div className={clsx(additionalContentClasses, classes.content)}>{children}</div>
    </Card>
  );
};

export default FormSectionCard;
